import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  loginNopass as loginNopassApi,
  loginSms as loginSmsApi,
  getUserInfo as getUserInfoApi,
  logout as logoutApi,
} from '@/api/user'
import type { LoginNopassParams, LoginParams, LoginResult, UserInfo } from '@/types/user'
import { useRequest } from 'vue-request'
import { useChatStore } from './chat'
import { useBillStore } from './bill'
import { useTabRedDotStore } from './tabRedDot'
import { clearToken, setToken } from '@/utils/auth'
import { useLocalStorage } from '@vueuse/core'
import { customStorage, getStorageItem } from '@/utils/storage'
import { isApp, tcDeleteData } from '@/utils/bridge'
import { hasSearchParam } from '@/utils/urlQuery'

/**
 * beforeHydrate 异步从 Native 加载数据的 Promise。
 * pinia-plugin-persistedstate 不会 await async beforeHydrate，
 * Mine/Account 这类强依赖当前用户信息的页面需要主动等待。
 */
let _hydrationPromise: Promise<void> = Promise.resolve()

/**
 * userInfo 写入版本号。
 * 如果接口已写入当前用户，后完成的旧 hydration 不能再覆盖它。
 */
let _userInfoVersion = 0

let _fetchUserInfoPromise: Promise<UserInfo | undefined> | null = null

const normalizeUserInfo = (info: UserInfo): UserInfo => {
  return {
    ...info,
    gender: info.sex == 1 ? '男' : info.sex == 0 ? '女' : '',
  }
}

export const useUserStore = defineStore('user', () => {
  // 根据 URL 参数 data 判断：有 data 参数则为联登 / 内嵌内页状态。
  const innerPage = useLocalStorage('qx_inner_page', false)
  if (hasSearchParam('data')) {
    innerPage.value = true
  }

  const userInfo = ref<UserInfo | null>(null)

  // 登录表单缓存（仅在当前会话中保留，用于协议页面返回后恢复）
  const loginMobile = ref('')
  const loginVerifyCode = ref('')
  const loginAgree = ref(false)

  // 设置用户信息
  const setInfo = (info: UserInfo) => {
    _userInfoVersion++
    userInfo.value = normalizeUserInfo(info)
  }

  const clearUserInfo = () => {
    _userInfoVersion++
    userInfo.value = null
  }

  // 短信登录
  const loginSms = async (loginForm: LoginParams) => {
    try {
      const data = await loginSmsApi(loginForm)

      setToken(data.token)
      setInfo({
        uuid: data.uuid,
        mobile: data.mobile,
        nickName: data.nickName || '',
        sex: data.sex,
      })

      // 登录成功后清除缓存的登录表单数据
      loginMobile.value = ''
      loginVerifyCode.value = ''
      loginAgree.value = false
      return data
    } catch (error) {
      clearAuth()
      throw error
    }
  }
  let retryCount = 0 // 记录当前重试次数
  const maxRetries = 2 // 最多重试2次（总共尝试3次）
  let loginResolve: ((value: LoginResult) => void) | null = null
  let loginReject: ((reason?: unknown) => void) | null = null

  type NopassExchangeRequest = {
    params: LoginNopassParams
    dongchaEntry: boolean
  }

  const loginNopassRequest = ({ params, dongchaEntry }: NopassExchangeRequest) => {
    return loginNopassApi(params, dongchaEntry)
  }
  const { run: loginNopass } = useRequest(loginNopassRequest, {
    manual: true,
    errorRetryCount: maxRetries, // 失败后重试2次
    onBefore: () => {
      console.log(`🔄 免密登录尝试 ${retryCount + 1}/${maxRetries + 1}`)
    },
    onSuccess: (data) => {
      console.log('✅ 免密登录成功')
      retryCount = 0 // 重置计数器
      setToken(data.token)
      setInfo({
        uuid: data.uuid,
        mobile: data.mobile,
        nickName: data.nickName,
        sex: data.sex,
      })
      // 通过 Promise 的 resolve 返回成功
      if (loginResolve) {
        loginResolve(data)
        loginResolve = null
        loginReject = null
      }
    },
    onError: (error) => {
      // ⚠️ 每次失败都会被调用
      retryCount++
      const axiosError = error as { response?: { status?: number } }
      const status = axiosError.response?.status
      console.error(`❌ 第 ${retryCount} 次尝试失败${status ? ` (HTTP ${status})` : ''}`)

      // 只有达到最大重试次数时才触发失败
      if (retryCount >= maxRetries + 1) {
        console.error(`❌ 免密登录最终失败（已尝试 ${retryCount} 次）`)
        clearAuth() // 清除认证信息
        // 通过 Promise 的 reject 返回失败
        if (loginReject) {
          loginReject(error)
          loginResolve = null
          loginReject = null
        }
      }
    },
  })

  const exchangeNopassToken = async (request: NopassExchangeRequest) => {
    retryCount = 0 // 重置计数器

    // 创建一个 Promise 来等待登录结果
    const loginPromise = new Promise<LoginResult>((resolve, reject) => {
      loginResolve = resolve
      loginReject = reject
    })

    // 触发登录请求（不等待结果，通过回调处理）
    loginNopass(request)

    // 等待 Promise 完成（通过 onSuccess 或 onError 回调触发）
    return loginPromise
  }

  // 普通联登保持原参数和行为。
  const exchangeToken = (secretKey: string) => exchangeNopassToken({
    params: { secretKey },
    dongchaEntry: false,
  })

  // Dongcha 仅替换请求参数和 Header，重试、Token 与用户信息写入复用同一流程。
  const exchangeDongchaToken = (params: LoginNopassParams) => exchangeNopassToken({
    params,
    dongchaEntry: true,
  })

  // 获取用户信息
  const fetchUserInfo = async (options?: { clearBeforeFetch?: boolean }) => {
    if (options?.clearBeforeFetch) {
      clearUserInfo()
    }

    if (_fetchUserInfoPromise) {
      return _fetchUserInfoPromise
    }

    _fetchUserInfoPromise = (async () => {
      const data = await getUserInfoApi()
      if (data) {
        // 如果接口返回了 token，更新 token
        if (data.token) {
          setToken(data.token)
        }
        setInfo(data)
      }
      return data
    })()

    try {
      return await _fetchUserInfoPromise
    } finally {
      _fetchUserInfoPromise = null
    }
  }

  // 退出登录
  const logout = async () => {
    try {
      await logoutApi()
    } finally {
      console.log('用户已退出登录')
      clearAuth(true)
    }
  }

  // 清除认证信息
  const clearAuth = (clearChatData = false) => {
    clearToken()
    clearUserInfo()
    useTabRedDotStore().reset()

    // 清除登录表单缓存
    loginMobile.value = ''
    loginVerifyCode.value = ''
    loginAgree.value = false

    // App 环境：批量清除 Native 存储的所有 store 数据
    if (isApp()) {
      tcDeleteData(['user-store', 'chat-store', 'bill-store', 'bill-detail-store', 'bill-platform-store'])
    }

    // 只有注销账号时才清空聊天数据
    if (clearChatData) {
      const chatStore = useChatStore()
      chatStore.clearAllChatData()
      const billStore = useBillStore()
      billStore.clearAllBillData()
    }
  }

  const waitForHydration = () => _hydrationPromise

  return {
    innerPage,
    userInfo,
    loginMobile,
    loginVerifyCode,
    loginAgree,
    loginSms,
    exchangeToken,
    exchangeDongchaToken,
    fetchUserInfo,
    logout,
    clearAuth,
    clearUserInfo,
    waitForHydration,
  }
},
  // 持久化配置（仅 App 环境启用）
  // H5 是 SPA，store 数据在内存中，页面跳转不会丢失，无需持久化
  // App 每个页面是独立 WebView，store 数据不共享，需要持久化到 Native
  isApp() ? {
    persist: {
      key: 'user-store',
      storage: customStorage,
      paths: ['userInfo', 'innerPage'],
      beforeHydrate(ctx) {
        const hydrateVersion = _userInfoVersion
        _hydrationPromise = (async () => {
          try {
            const savedState = await getStorageItem('user-store')
            if (savedState) {
              const state = JSON.parse(savedState)
              // 只恢复指定的字段；如果接口已写入当前用户，不允许旧 hydration 覆盖。
              if (
                Object.prototype.hasOwnProperty.call(state, 'userInfo') &&
                _userInfoVersion === hydrateVersion
              ) {
                ctx.store.userInfo = state.userInfo
                  ? normalizeUserInfo(state.userInfo)
                  : null
              }
              if (state.innerPage !== undefined) ctx.store.innerPage = state.innerPage
            }
          } catch (error) {
            console.error('从 Native 加载 userStore 数据失败:', error)
          }
        })()
      },
    },
  } : {}
)
